import './Credentials.css';
import type { JSX } from 'preact';
import {
  accessPath, type AccessGrant,
} from './CredentialsApi';
import { ErrorNotice, useCredentialResource } from './CredentialsParts';
import { CredentialsContent } from './CredentialsPage';
import type { McpAuthentication } from '../../../../shared/mcp-authentication';

export function GroupCredentials({ gid }: { gid: string }): JSX.Element {
  return <CredentialsContent agent={{ id: gid }} />;
}

import { serviceMatchesResource } from '../../../../shared/credential-matching';
export { serviceMatchesResource } from '../../../../shared/credential-matching';

/** MCP only displays grant references. Authentication is created in the personal connection flow. */
export function McpConnectionAccess({ gid, resourceUrl, onOpenCredentials, authentication, onAuthenticationChange, serverName, disabled }: {
  gid: string; resourceUrl: string; onOpenCredentials: (create: boolean, connectionId?: string) => void;
  authentication?: McpAuthentication;
  onAuthenticationChange?: (authentication: McpAuthentication | undefined) => void;
  serverName?: string;
  disabled?: boolean;
}): JSX.Element {
  const { data, error, loading, refresh } = useCredentialResource<{ grants: AccessGrant[] }>(accessPath(gid));
  const matches = data?.grants.flatMap((grant) => grant.services.filter((service) => serviceMatchesResource(service, resourceUrl))
    .map((service) => ({ grant, service }))) ?? [];
  const choices = [...new Map(matches.map(item => [item.grant.connection_id, item.grant])).values()];
  return <div class="ga-cred-mcp-auth">
    <span class="ga-mcp-row-label">Account</span>
    {loading ? <span class="muted">Loading available accounts…</span> : null}
    <ErrorNotice message={error} />
    {onAuthenticationChange && !loading && !error ? <>
      {choices.length ? (
        <select class="ga-mcp-input" disabled={disabled} aria-label={`Account for ${serverName ? `${serverName} tools` : 'these tools'}`} value={authentication?.connection_id ?? ''}
          onChange={event => {
            const selected = choices.find(choice => choice.connection_id === event.currentTarget.value);
            onAuthenticationChange(selected ? { connection_id: selected.connection_id,
              account_alias: authentication?.account_alias || serverName || 'Account' } : undefined);
          }}>
          <option value="">Choose an account automatically</option>
          {authentication && !choices.length
            ? <option value={authentication.connection_id}>Saved account — not available to this agent</option> : null}
          {choices.map(choice => <option key={choice.connection_id} value={choice.connection_id} disabled={choice.status !== 'active'}>
            {choice.label}{choice.status === 'active' ? '' : ` (${choice.status})`}
          </option>)}
        </select>
      ) : null}
      {authentication && !choices.some(choice => choice.connection_id === authentication.connection_id)
        ? <p class="muted">Your saved account is not available to this agent.</p> : null}
      {authentication ? <label>Name for this account in these tools
        <input class="ga-mcp-input" disabled={disabled} aria-label={`Account name for ${serverName ? `${serverName} tools` : 'these tools'}`} value={authentication.account_alias} maxLength={80}
          onInput={event => onAuthenticationChange({ ...authentication, account_alias: event.currentTarget.value })} />
        <span class="muted">Helps the agent tell your accounts apart. Selecting an account does not give the agent permission to use it.</span>
      </label> : null}
    </> : null}
    {error ? <button type="button" onClick={() => void refresh()}>Retry access</button> : null}
    {!loading && !error ? matches.length ? <ul>{matches.map(({ grant, service }) =>
      <li key={`${grant.connection_id}:${service.name}`}>{grant.label} / {service.name} · {grant.status}
        {' · Owner: '}{grant.owner.label ?? grant.owner.id}{service.enabled === false ? ' · service disabled' : ''}</li>)}</ul>
      : <span class="muted">No matching account is available to this agent. Some tools do not need an account.</span> : null}
    <button type="button" disabled={loading || !!error} onClick={() => onOpenCredentials(!matches.length, matches.length === 1 ? matches[0].grant.connection_id : undefined)}>
      {matches.length ? 'Manage your accounts' : 'Add an account'}
    </button>
  </div>;
}
